// src/services/api.js - Debug version
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

console.log('API URL:', API_URL); // Debug log

// Create axios instance with base URL
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Session storage cache utilities for opt-out status
const OPT_OUT_CACHE_KEY = 'optOutCache';
const cacheOptOutStatus = (phoneNumber, status) => {
  const cache = JSON.parse(sessionStorage.getItem(OPT_OUT_CACHE_KEY) || '{}');
  cache[phoneNumber] = { ...status, cachedAt: Date.now() };
  sessionStorage.setItem(OPT_OUT_CACHE_KEY, JSON.stringify(cache));
};

const getCachedOptOutStatus = (phoneNumber) => {
  const cache = JSON.parse(sessionStorage.getItem(OPT_OUT_CACHE_KEY) || '{}');
  return cache[phoneNumber];
};

const clearOptOutCache = (phoneNumber = null) => {
  if (phoneNumber) {
    const cache = JSON.parse(sessionStorage.getItem(OPT_OUT_CACHE_KEY) || '{}');
    delete cache[phoneNumber];
    sessionStorage.setItem(OPT_OUT_CACHE_KEY, JSON.stringify(cache));
  } else {
    sessionStorage.removeItem(OPT_OUT_CACHE_KEY);
  }
};

// Utility function to extract meaningful error messages
const extractErrorMessage = (error) => {
  // If server provided a specific message, use it
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  // Handle common HTTP status codes
  const status = error.response?.status;
  switch (status) {
    case 400:
      return error.response?.data?.error || 'Invalid request. Please check your input.';
    case 401:
      return 'Authentication required. Please log in again.';
    case 403:
      return 'Access denied. You don\'t have permission for this action.';
    case 404:
      return 'Resource not found.';
    case 409:
      return error.response?.data?.error || 'Conflict with existing data.';
    case 429:
      return 'Too many requests. Please try again later.';
    case 500:
      return 'Server error. Please try again or contact support.';
    default:
      return error.message || 'An unexpected error occurred.';
  }
};

// Add response interceptor for error handling
api.interceptors.response.use(
  response => {
    console.log('API Response:', response.data); // Debug log
    return response;
  },
  error => {
    console.error('API Error:', error);
    if (error.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
      
      // Handle 401 unauthorized - redirect to login
      if (error.response.status === 401) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }

      // Handle account gating (pending/suspended) from auth middleware
      // Only redirect if user had a token (mid-session suspension), not during login/signup
      const code = error.response.data?.code;
      if (error.response.status === 403 && (code === 'ACCOUNT_PENDING' || code === 'ACCOUNT_SUSPENDED')) {
        if (localStorage.getItem('token')) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          window.location.href = '/login';
        }
      }
    } else if (error.request) {
      console.error('Request error:', error.request);
    }
    return Promise.reject(error);
  }
);

// Add authorization token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    console.log('Request:', config.method.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Authentication services
const authService = {
  register: async (userData) => {
    console.log('Registering user:', userData.email);
    const response = await api.post('/auth/register', userData);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
    }
    return response.data;
  },
  
  login: async (credentials) => {
    console.log('Logging in user:', credentials.email);
    const response = await api.post('/auth/login', credentials);
    if (response.data.token) {
      localStorage.setItem('token', response.data.token);
      // Store user data for profile display
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }
    }
    return response.data;
  },
  
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
  
  getCurrentUser: () => {
    return JSON.parse(localStorage.getItem('user'));
  },
  
  isAuthenticated: () => {
    const token = localStorage.getItem('token');
    if (!token) return false;

    try {
      // Check if token is expired (simple JWT decode)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);

      if (payload.exp && payload.exp < currentTime) {
        // Token expired, remove it
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        return false;
      }

      return true;
    } catch (error) {
      // Invalid token format, remove it
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      return false;
    }
  },

  getProfile: async () => {
    const response = await api.get('/auth/profile');
    // Update local storage with fresh user data
    if (response.data) {
      localStorage.setItem('user', JSON.stringify(response.data));
    }
    return response.data;
  },

  updateProfile: async (profileData) => {
    const response = await api.put('/auth/profile', profileData);
    // Update local storage with updated user data
    if (response.data) {
      localStorage.setItem('user', JSON.stringify(response.data));
    }
    return response.data;
  },

  getTierUsage: async () => {
    const response = await api.get('/auth/tier-usage');
    return response.data;
  }
};

// Event services
const eventService = {
  getAllEvents: async () => {
    try {
      console.log('Fetching events from:', `${API_URL}/events`);
      const response = await api.get('/events');
      console.log('Events response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in getAllEvents:', error);
      throw error;
    }
  },
  
  getEvent: async (eventId) => {
    const response = await api.get(`/events/${eventId}`);
    return response.data;
  },

  // BUNDLED API: Get event with all related data in one call (Performance optimization)
  getEventFullDetails: async (eventId) => {
    const response = await api.get(`/events/${eventId}/full-details`);
    return response.data;
  },
  
  createEvent: async (eventData) => {
    const response = await api.post('/events', eventData);
    return response.data;
  },
  
  updateEvent: async (eventId, eventData) => {
    const response = await api.put(`/events/${eventId}`, eventData);
    return response.data;
  },
  
  deleteEvent: async (eventId) => {
    const response = await api.delete(`/events/${eventId}`);
    return response.data;
  },

  getActivityLogs: async (eventId) => {
    const response = await api.get(`/events/${eventId}/activity-logs`);
    return response.data;
  },

  getPackageHistory: async (eventId) => {
    const response = await api.get(`/events/${eventId}/package-history`);
    return response.data;
  },

  getDeliveryStats: async (eventId) => {
    const response = await api.get(`/events/${eventId}/delivery-stats`);
    return response.data;
  },

  // Schedule Events
  addScheduleEvent: async (eventId, scheduleEventData) => {
    const response = await api.post(`/events/${eventId}/schedule-events`, scheduleEventData);
    return response.data;
  },

  updateScheduleEvent: async (eventId, scheduleIndex, scheduleEventData) => {
    const response = await api.put(`/events/${eventId}/schedule-events/${scheduleIndex}`, scheduleEventData);
    return response.data;
  },

  deleteScheduleEvent: async (eventId, scheduleIndex) => {
    const response = await api.delete(`/events/${eventId}/schedule-events/${scheduleIndex}`);
    return response.data;
  },

  // Event Info / FAQ
  getEventInfo: async (eventId) => {
    const response = await api.get(`/events/${eventId}/event-info`);
    return response.data;
  },

  addEventInfo: async (eventId, eventInfoData) => {
    const response = await api.post(`/events/${eventId}/event-info`, eventInfoData);
    return response.data;
  },

  updateEventInfo: async (eventId, infoIndex, eventInfoData) => {
    const response = await api.put(`/events/${eventId}/event-info/${infoIndex}`, eventInfoData);
    return response.data;
  },

  deleteEventInfo: async (eventId, infoIndex) => {
    const response = await api.delete(`/events/${eventId}/event-info/${infoIndex}`);
    return response.data;
  },

  uploadEventInfoFile: async (eventId, file) => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(`/events/${eventId}/event-info/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Event Sharing and Invitations
  inviteUser: async (eventId, invitationData) => {
    const response = await api.post(`/events/${eventId}/invite`, invitationData);
    return response.data;
  },

  getCollaborators: async (eventId) => {
    const response = await api.get(`/events/${eventId}/collaborators`);
    return response.data;
  },

  acceptInvitation: async (token) => {
    const response = await api.post(`/invite/${token}/accept`);
    return response.data;
  },

  getInvitationDetails: async (token) => {
    const response = await api.get(`/invite/${token}`);
    return response.data;
  },

  // Notes
  addNote: async (eventId, noteData) => {
    const response = await api.post(`/events/${eventId}/notes`, noteData);
    return response.data;
  },

  updateNote: async (eventId, noteId, noteData) => {
    const response = await api.put(`/events/${eventId}/notes/${noteId}`, noteData);
    return response.data;
  },

  deleteNote: async (eventId, noteId) => {
    const response = await api.delete(`/events/${eventId}/notes/${noteId}`);
    return response.data;
  },

  // Tab Configuration
  updateTabConfiguration: async (eventId, hiddenTabs) => {
    const response = await api.put(`/events/${eventId}/tab-config`, { hiddenTabs });
    return response.data;
  }
};

// Guest services
const guestService = {
  addGuest: async (eventId, guestData) => {
    const response = await api.post(`/events/${eventId}/guests`, guestData);
    return response.data;
  },

  updateGuest: async (guestId, guestData) => {
    const response = await api.put(`/${guestId}`, guestData);
    return response.data;
  },

  deleteGuest: async (guestId) => {
    const response = await api.delete(`/${guestId}`);
    return response.data;
  },

  bulkDeleteGuests: async (guestIds) => {
    const response = await api.delete('/bulk', { data: { guestIds } });
    return response.data;
  },

  bulkAddGuests: async (eventId, guestsArray, skipDuplicates) => {
    const payload = { guests: guestsArray };
    if (skipDuplicates !== undefined) {
      payload.skipDuplicates = skipDuplicates;
    }
    const response = await api.post(`/events/${eventId}/guests/bulk`, payload);
    return response.data;
  }
};

// Alert Rule services
const alertRuleService = {
  getAllRules: async (eventId) => {
    const response = await api.get(`/events/${eventId}/alert-rules`);
    return response.data;
  },
  
  previewRule: async (eventId, ruleData) => {
    const response = await api.post(`/events/${eventId}/rules/preview`, ruleData);
    return response.data;
  },
  
  createRule: async (eventId, ruleData) => {
    const response = await api.post(`/events/${eventId}/alert-rules`, ruleData);
    return response.data;
  },
  
  previewRuleEdit: async (ruleId, ruleData) => {
    const response = await api.put(`/alert-rules/${ruleId}/preview`, ruleData);
    return response.data;
  },
  
  updateRule: async (ruleId, ruleData) => {
    const response = await api.put(`/alert-rules/${ruleId}`, ruleData);
    return response.data;
  },
  
  deleteRule: async (ruleId) => {
    const response = await api.delete(`/alert-rules/${ruleId}`);
    return response.data;
  },
  
  toggleRule: async (ruleId) => {
    const response = await api.patch(`/alert-rules/${ruleId}/toggle`);
    return response.data;
  }
};

// Vendor Service
export const vendorService = {
  // Get all vendors for the current planner
  getAllVendors: async () => {
    const response = await api.get('/vendors');
    return response.data;
  },

  // Create new vendor
  createVendor: async (vendorData) => {
    const response = await api.post('/vendors', vendorData);
    return response.data;
  },

  // Update vendor
  updateVendor: async (vendorId, vendorData) => {
    const response = await api.put(`/vendors/${vendorId}`, vendorData);
    return response.data;
  },

  // Delete vendor
  deleteVendor: async (vendorId) => {
    const response = await api.delete(`/vendors/${vendorId}`);
    return response.data;
  },

  // Get vendor assignments for an event
  getEventVendors: async (eventId) => {
    const response = await api.get(`/events/${eventId}/vendors`);
    return response.data;
  },

  // Assign vendor to event
  assignVendorToEvent: async (eventId, assignmentData) => {
    const response = await api.post(`/events/${eventId}/vendors`, assignmentData);
    return response.data;
  },

  // Update vendor assignment
  updateVendorAssignment: async (eventId, assignmentId, updateData) => {
    const response = await api.put(`/events/${eventId}/vendors/${assignmentId}`, updateData);
    return response.data;
  },

  // Remove vendor assignment
  removeVendorAssignment: async (eventId, assignmentId) => {
    const response = await api.delete(`/events/${eventId}/vendors/${assignmentId}`);
    return response.data;
  },

  // Send alert to vendor assignment
  sendVendorAlert: async (assignmentId) => {
    try {
      const response = await api.post(`/vendor-assignments/${assignmentId}/send-alert`);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  }
};

// Timeline Service
const timelineService = {
  // Get timeline tasks for an event
  getEventTasks: async (eventId) => {
    const response = await api.get(`/events/${eventId}/timeline`);
    return response.data;
  },

  // Create new timeline task
  createTask: async (eventId, taskData) => {
    const response = await api.post(`/events/${eventId}/timeline`, taskData);
    return response.data;
  },

  // Update timeline task
  updateTask: async (eventId, taskId, taskData) => {
    const response = await api.put(`/events/${eventId}/timeline/${taskId}`, taskData);
    return response.data;
  },

  // Delete timeline task
  deleteTask: async (eventId, taskId) => {
    const response = await api.delete(`/events/${eventId}/timeline/${taskId}`);
    return response.data;
  },
  
  // Send manual alert for a specific task
  sendTaskAlert: async (taskId) => {
    try {
      const response = await api.post(`/tasks/${taskId}/send-alert`);
      return response.data;
    } catch (error) {
      // Enhanced error handling with specific messages
      throw new Error(extractErrorMessage(error));
    }
  },
  
  // Check if a contact has opted out of WhatsApp messages (with caching)
  checkOptOutStatus: async (phoneNumber) => {
    // Check cache first
    const cachedStatus = getCachedOptOutStatus(phoneNumber);
    if (cachedStatus) {
      return cachedStatus;
    }
    
    // Fetch from API and cache result
    const encodedPhone = encodeURIComponent(phoneNumber);
    const response = await api.get(`/contacts/${encodedPhone}/opt-out-status`);
    cacheOptOutStatus(phoneNumber, response.data);
    return response.data;
  },
  
  // Clear opt-out cache for a specific contact (use when STOP received)
  clearOptOutCache: (phoneNumber) => clearOptOutCache(phoneNumber)
};

// Checklist Service
const checklistService = {
  // Get categories for an event
  getCategories: async (eventId) => {
    const response = await api.get(`/events/${eventId}/categories`);
    return response.data;
  },

  // Update categories for an event
  updateCategories: async (eventId, data) => {
    const response = await api.put(`/events/${eventId}/categories`, data);
    return response.data;
  },

  // Get all tasks for an event
  getTasks: async (eventId, params = {}) => {
    const response = await api.get(`/events/${eventId}/tasks`, { params });
    return response.data;
  },

  // Create a new task
  createTask: async (eventId, task) => {
    const response = await api.post(`/events/${eventId}/tasks`, task);
    return response.data;
  },

  // Update a task
  updateTask: async (eventId, taskId, updates) => {
    const response = await api.put(`/events/${eventId}/tasks/${taskId}`, updates);
    return response.data;
  },

  // Delete a task
  deleteTask: async (eventId, taskId) => {
    const response = await api.delete(`/events/${eventId}/tasks/${taskId}`);
    return response.data;
  },

  // Bulk create tasks
  bulkCreateTasks: async (eventId, tasks) => {
    const response = await api.post(`/events/${eventId}/tasks/bulk`, { tasks });
    return response.data;
  },

  // Get task templates
  getTemplates: async () => {
    const response = await api.get('/events/templates');
    return response.data;
  }
};

// Message Tracking Service
const messageTrackingService = {
  // Get message delivery status for an event
  getMessageStatus: async (eventId) => {
    const response = await api.get(`/events/${eventId}/message-status`);
    return response.data;
  },

  // Get failed messages for an event
  getFailedMessages: async (eventId) => {
    const response = await api.get(`/events/${eventId}/failed-messages`);
    return response.data;
  },

  // Get opted-out users for an event
  getOptedOutUsers: async (eventId) => {
    const response = await api.get(`/events/${eventId}/opted-out-users`);
    return response.data;
  },

  // Retry failed message
  retryMessage: async (messageId) => {
    const response = await api.post(`/messages/${messageId}/retry`);
    return response.data;
  }
};

// Custom Message Service
const customMessageService = {
  // Send RSVP template messages to selected guests (uses proper Quick Reply template)
  sendRsvpMessages: async (eventId, guestIds, templateId = 'rsvp_invite', sendImmediately = true) => {
    const response = await api.post(`/events/${eventId}/messages/send-bulk`, {
      guestIds,
      templateId,
      sendImmediately
    });
    return response.data;
  },

  // Upload media file for custom messages
  uploadMedia: async (formData) => {
    // Create a separate axios instance for file upload to avoid JSON content-type
    const uploadApi = axios.create({
      baseURL: API_URL,
      headers: {
        // Don't set Content-Type, let browser set it with boundary for multipart
      }
    });

    // Add auth header manually
    const token = localStorage.getItem('token');
    if (token) {
      uploadApi.defaults.headers.Authorization = `Bearer ${token}`;
    }

    const response = await uploadApi.post('/upload-media', formData);
    return response.data;
  },

  // Send custom message to filtered recipients
  sendCustomMessage: async (eventId, customMessageData) => {
    const response = await api.post(`/events/${eventId}/custom-message`, customMessageData);
    return response.data;
  },

  // Get scheduled custom messages for an event
  getScheduledMessages: async (eventId, params = {}) => {
    const response = await api.get(`/events/${eventId}/scheduled-custom-messages`, { params });
    return response.data;
  },

  // Cancel a scheduled custom message
  cancelScheduledMessage: async (jobId) => {
    const response = await api.delete(`/scheduled-messages/${jobId}`);
    return response.data;
  },

  // Get message batches with delivery stats
  getMessageBatches: async (eventId) => {
    const response = await api.get(`/events/${eventId}/message-batches`);
    return response.data;
  },

  // Cancel all pending jobs in a batch
  cancelBatch: async (batchId) => {
    const response = await api.post(`/message-batches/${batchId}/cancel`);
    return response.data;
  },

  // Get per-recipient details for a batch
  getBatchDetails: async (batchId) => {
    const response = await api.get(`/message-batches/${batchId}/details`);
    return response.data;
  }
};

// User Messages Service (Planner Inbox)
const userMessageService = {
  // Get user messages for an event
  getMessages: async (eventId, status = null, limit = 50) => {
    const params = { limit };
    if (status) params.status = status;
    
    const response = await api.get(`/events/${eventId}/user-messages`, { params });
    return response.data;
  },

  // Get unread count for an event
  getUnreadCount: async (eventId) => {
    const response = await api.get(`/events/${eventId}/user-messages/unread-count`);
    return response.data;
  },

  // Mark message as read
  markAsRead: async (messageId) => {
    const response = await api.put(`/user-messages/${messageId}/read`);
    return response.data;
  },

  // Bulk mark all messages from a phone number as read within an event
  markConversationRead: async (eventId, phoneNumber) => {
    const response = await api.put(`/events/${eventId}/user-messages/mark-read`, { phoneNumber });
    return response.data;
  },

  // Mark message as responded with notes
  markAsResponded: async (messageId, responseNotes = '') => {
    const response = await api.put(`/user-messages/${messageId}/respond`, { responseNotes });
    return response.data;
  },

  // Get combined message outbox (sent + received messages)
  getMessageOutbox: async (eventId, limit = 100) => {
    const response = await api.get(`/events/${eventId}/message-outbox`, { params: { limit } });
    return response.data;
  }
};

// Groups Service
const groupService = {
  // Get all groups for an event
  getGroups: async (eventId, includeArchived = false) => {
    const response = await api.get(`/events/${eventId}/groups`, {
      params: { includeArchived }
    });
    return response.data;
  },

  // Create a new group
  createGroup: async (eventId, groupData) => {
    const response = await api.post(`/events/${eventId}/groups`, groupData);
    return response.data;
  },

  // Update a group
  updateGroup: async (eventId, groupId, groupData) => {
    const response = await api.put(`/events/${eventId}/groups/${groupId}`, groupData);
    return response.data;
  },

  // Delete a group
  deleteGroup: async (eventId, groupId) => {
    const response = await api.delete(`/events/${eventId}/groups/${groupId}`);
    return response.data;
  },

  // Add members to a group
  addMembersToGroup: async (eventId, groupId, guestIds) => {
    const response = await api.post(`/events/${eventId}/groups/${groupId}/members`, {
      guestIds
    });
    return response.data;
  },

  // Remove members from a group
  removeMembersFromGroup: async (eventId, groupId, guestIds) => {
    const response = await api.delete(`/events/${eventId}/groups/${groupId}/members`, {
      data: { guestIds }
    });
    return response.data;
  },

  // Get group members
  getGroupMembers: async (eventId, groupId, limit = 100, skip = 0) => {
    const response = await api.get(`/events/${eventId}/groups/${groupId}/members`, {
      params: { limit, skip }
    });
    return response.data;
  },

  // Bulk assign guests to multiple groups
  bulkAssignToGroups: async (eventId, guestIds, groupIds, operation = 'add') => {
    const response = await api.post(`/events/${eventId}/groups/bulk-assign`, {
      guestIds,
      groupIds,
      operation
    });
    return response.data;
  },

  // Create default groups
  createDefaultGroups: async (eventId) => {
    const response = await api.post(`/events/${eventId}/groups/create-defaults`);
    return response.data;
  }
};

// Task Groups Service
const taskGroupService = {
  // Get all task groups for an event
  getGroups: async (eventId, includeArchived = false) => {
    const response = await api.get(`/events/${eventId}/task-groups`, {
      params: { includeArchived }
    });
    return response.data;
  },

  // Create new task group
  createGroup: async (eventId, groupData) => {
    const response = await api.post(`/events/${eventId}/task-groups`, groupData);
    return response.data;
  },

  // Update task group
  updateGroup: async (eventId, groupId, groupData) => {
    const response = await api.put(`/events/${eventId}/task-groups/${groupId}`, groupData);
    return response.data;
  },

  // Delete task group
  deleteGroup: async (eventId, groupId) => {
    const response = await api.delete(`/events/${eventId}/task-groups/${groupId}`);
    return response.data;
  },

  // Add tasks to group
  addTasks: async (eventId, groupId, taskIds) => {
    const response = await api.post(`/events/${eventId}/task-groups/${groupId}/tasks`, { taskIds });
    return response.data;
  },

  // Remove tasks from group
  removeTasks: async (eventId, groupId, taskIds) => {
    const response = await api.delete(`/events/${eventId}/task-groups/${groupId}/tasks`, {
      data: { taskIds }
    });
    return response.data;
  },

  // Get tasks in a group
  getGroupTasks: async (eventId, groupId) => {
    const response = await api.get(`/events/${eventId}/task-groups/${groupId}/tasks`);
    return response.data;
  },

  // Bulk assign tasks to groups
  bulkAssign: async (eventId, assignments) => {
    const response = await api.post(`/events/${eventId}/task-groups/bulk-assign`, assignments);
    return response.data;
  },

  // Create default task groups
  createDefaultGroups: async (eventId) => {
    const response = await api.post(`/events/${eventId}/task-groups/create-defaults`);
    return response.data;
  }
};

// Job Management Service
const jobManagementService = {
  // Get all jobs for an event with pagination and filters
  getJobs: async (eventId, params = {}) => {
    const response = await api.get(`/events/${eventId}/outbox`, { params });
    return response.data;
  },

  // Cancel a specific job
  cancelJob: async (jobId) => {
    const response = await api.delete(`/jobs/${jobId}`);
    return response.data;
  },

  // Retry a failed job
  retryJob: async (jobId) => {
    const response = await api.post(`/jobs/${jobId}/retry`);
    return response.data;
  },

  // Send job immediately
  sendNow: async (jobId) => {
    const response = await api.post(`/jobs/${jobId}/send-now`);
    return response.data;
  },

  // Get failed/undelivered messages for debugging
  getFailedMessages: async (eventId, params = {}) => {
    const response = await api.get(`/events/${eventId}/failed-messages`, { params });
    return response.data;
  },

  // Get delivery statistics for an event
  getDeliveryStats: async (eventId) => {
    const response = await api.get(`/events/${eventId}/delivery-stats`);
    return response.data;
  }
};

// Guest Question Service
const guestQuestionService = {
  // Get all guest questions for an event
  getQuestions: async (eventId, params = {}) => {
    const response = await api.get(`/events/${eventId}/guest-questions`, { params });
    return response.data;
  },

  // Get unanswered count
  getUnansweredCount: async (eventId) => {
    const response = await api.get(`/events/${eventId}/guest-questions/unanswered-count`);
    return response.data;
  },

  // Answer a question
  answerQuestion: async (questionId, data) => {
    const response = await api.put(`/guest-questions/${questionId}/answer`, data);
    return response.data;
  },

  // Dismiss a question
  dismissQuestion: async (questionId) => {
    const response = await api.put(`/guest-questions/${questionId}/dismiss`);
    return response.data;
  }
};

// Questionnaire Service
const questionnaireService = {
  // Get questionnaire responses for all guests in an event
  getResponses: async (eventId) => {
    const response = await api.get(`/events/${eventId}/questionnaire-responses`);
    return response.data;
  }
};

// Finance Service
const financeService = {
  // Apply finance manual override
  applyFinanceOverride: async (eventId, data) => {
    try {
      const response = await api.put(`/events/${eventId}/packages/finance-override`, data);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  },

  // Remove finance override
  removeFinanceOverride: async (eventId) => {
    try {
      const response = await api.delete(`/events/${eventId}/packages/finance-override`);
      return response.data;
    } catch (error) {
      throw new Error(extractErrorMessage(error));
    }
  }
};

// Messaging service for bulk RSVP and custom messages
export const messagingService = {
  // Send RSVP template messages to guests
  sendRsvpMessages: async (eventId, payload) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`/events/${eventId}/messages/rsvp`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error sending RSVP messages:', error);
      throw error;
    }
  },

  // Send custom messages to guests
  sendCustomMessages: async (eventId, payload) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`/events/${eventId}/messages/custom`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error sending custom messages:', error);
      throw error;
    }
  },

  // Send bulk messages with template selection (simplified API)
  sendBulkMessage: async (eventId, data) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`/events/${eventId}/messages/send-bulk`, data, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error sending bulk messages:', error);
      throw error;
    }
  }
};

// Template Service for managing WhatsApp templates
export const templateService = {
  // List all templates from Twilio
  listTemplates: async () => {
    const response = await api.get('/templates');
    return response.data;
  },

  // Get a specific template
  getTemplate: async (sid) => {
    const response = await api.get(`/templates/${sid}`);
    return response.data;
  },

  // Create a utility template
  createUtilityTemplate: async (data) => {
    const response = await api.post('/templates/utility', data);
    return response.data;
  },

  // Create a quick reply template with buttons
  createQuickReplyTemplate: async (data) => {
    const response = await api.post('/templates/quick-reply', data);
    return response.data;
  },

  // Submit template for WhatsApp approval
  submitForApproval: async (sid) => {
    const response = await api.post(`/templates/${sid}/approve`);
    return response.data;
  },

  // Get approval status
  getApprovalStatus: async (sid) => {
    const response = await api.get(`/templates/${sid}/approval-status`);
    return response.data;
  },

  // Delete a template
  deleteTemplate: async (sid) => {
    const response = await api.delete(`/templates/${sid}`);
    return response.data;
  }
};

// Package Catalog and Selection Services
export const packageService = {
  // Get venue catalog (attempts J49 API, falls back gracefully)
  getCatalog: async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/v1/packages/catalog', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data.packages;
    } catch (error) {
      console.warn('[Package Service] Catalog API unavailable, will use fallback');
      return null; // Component will use hardcoded fallback
    }
  },

  // Get specific package by ID
  getPackage: async (packageId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/v1/packages/catalog/${packageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.data.package;
    } catch (error) {
      console.error('Error fetching package:', error);
      throw error;
    }
  },

  // Get current event package selections
  getEventPackages: async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/events/${eventId}/packages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data.packages;
    } catch (error) {
      console.error('Error fetching event packages:', error);
      throw error;
    }
  },

  // Save wizard selections
  saveWizardSelections: async (eventId, wizardData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`/events/${eventId}/packages/wizard`,
        { wizardData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Error saving package selections:', error);
      throw error;
    }
  },

  // Calculate price for package
  calculatePrice: async (packageId, guestCount, eventDate = null) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post('/v1/packages/calculate-price',
        { packageId, guestCount, eventDate },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data.data.calculatedPrice;
    } catch (error) {
      console.error('Error calculating price:', error);
      throw error;
    }
  }
};

// Web Chat Service - Planner side (authenticated)
const webChatService = {
  // Generate a chat link for a guest
  generateChatLink: async (eventId, guestData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`/events/${eventId}/web-chat/generate`, guestData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error generating chat link:', error);
      throw error;
    }
  },

  // Get all chats for an event
  getEventChats: async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/events/${eventId}/web-chats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching event chats:', error);
      throw error;
    }
  },

  // Get specific chat with messages
  getChat: async (eventId, chatId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/events/${eventId}/web-chats/${chatId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching chat:', error);
      throw error;
    }
  },

  // Send message as planner
  sendMessage: async (eventId, chatId, content) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`/events/${eventId}/web-chats/${chatId}/messages`,
        { content },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }
};

// Conversation Service - WhatsApp inbox for viewing all guest conversations
const conversationService = {
  // Get all conversations for an event
  getConversations: async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/events/${eventId}/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching conversations:', error);
      throw error;
    }
  },

  // Get messages for a specific conversation/guest
  getMessages: async (eventId, guestId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/events/${eventId}/conversations/${guestId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching messages:', error);
      throw error;
    }
  },

  // Send a message to a guest
  sendMessage: async (eventId, guestId, messageData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.post(`/events/${eventId}/conversations/${guestId}/messages`, messageData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }
};

// Public Chat Service - Guest side (no auth required)
const publicChatService = {
  // Get chat by token (for guest view)
  getChat: async (chatToken) => {
    try {
      const response = await api.get(`/public/chat/${chatToken}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching public chat:', error);
      throw error;
    }
  },

  // Send message as guest
  sendMessage: async (chatToken, content) => {
    try {
      const response = await api.post(`/public/chat/${chatToken}/messages`, { content });
      return response.data;
    } catch (error) {
      console.error('Error sending guest message:', error);
      throw error;
    }
  },

  // Poll for new messages
  pollMessages: async (chatToken, since = null) => {
    try {
      const url = since
        ? `/public/chat/${chatToken}/poll?since=${encodeURIComponent(since)}`
        : `/public/chat/${chatToken}/poll`;
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('Error polling messages:', error);
      throw error;
    }
  }
};

const adminService = {
  getUsers: async (params = {}) => {
    const response = await api.get('/admin/users', { params });
    return response.data;
  },

  activateUser: async (userId, subscriptionTier = 'starter') => {
    const response = await api.post(`/admin/users/${userId}/activate`, { subscriptionTier });
    return response.data;
  },

  suspendUser: async (userId) => {
    const response = await api.post(`/admin/users/${userId}/suspend`);
    return response.data;
  },

  updateTier: async (userId, subscriptionTier) => {
    const response = await api.post(`/admin/users/${userId}/update-tier`, { subscriptionTier });
    return response.data;
  }
};

export {
  authService,
  eventService,
  guestService,
  alertRuleService,
  timelineService,
  checklistService,
  messageTrackingService,
  customMessageService,
  userMessageService,
  groupService,
  taskGroupService,
  jobManagementService,
  guestQuestionService,
  questionnaireService,
  financeService,
  webChatService,
  publicChatService,
  conversationService,
  adminService
};